/**
 * @fileoverview Core module for handling Universal ID logic, this file:
 * 1) Handles the creation of the uID and hpo_uID cookies at a browser level.
 * 2) Assumes that uID and hpo_uID cookies are categorized as 'Strictly Necessary' from a privacy standpoint.
 * 3) Includes utility functions for:
 *      - Detecting a user's browser
 *      - Checked whitelisted paths
 *      - Capturing user profile data
 *      - Managing a user's visit session
 *      - initializing the Universal ID flow
 * 4) Provides a function to send PII Data from a page-level (sendFormData)
 * @author Jaskiran Chhokar
 */

import captureAndSendGeoData from './api/getAndSendGeoData.ts';
import {
  captureConsentCookies,
  captureCookies,
  getCookieValue,
} from './collectors/collectBrowserCookies.ts';
import defaultUrlWhitelist from './constants/whitelistedPaths.ts';
import getAndSendFormData from './api/getAndSendFormData.ts';
import getMainDomainFromCurrentUrl from './collectors/collectDomain.ts';
import getUniversalIdCookie from './api/getUniversalIdCookie.ts';
import sendDataToConnectorGrid from './api/sendData.ts';
import userProfileData from './constants/payload.ts';
import { UidNamespace, ResponseBody } from 'globalTypes';
import {
  detectBrowser,
  isWhitelisted,
  setUniversalIdVisitSession,
} from './utils';
import { waitForOneTrustBanner } from './utils/helpers/oneTrustMutation.ts';

/* Initialize global universalId object if it doesn't exist */
window.universalId = window.universalId || ({} as UidNamespace);

/**
 * Sends PII data - formatted as an object - from public sites forms to Connector Grid.
 * Example of formDataObject: { firstname: hannah, lastname: montana}
 *
 * @async
 * @function
 * @name window.universalId.sendFormData
 * @param {Object} formDataObject
 * @returns {Promise<void|any>} - A promise that resolves once user profile data is sent.
 */
window.universalId.sendFormData = async (
  formDataObject: object
): Promise<void> => {
  return new Promise(async (resolve) => {
    captureBrowserInfo(userProfileData);
    captureCookies(userProfileData);
    await captureAndSendGeoData(userProfileData);
    resolve(await getAndSendFormData(formDataObject, userProfileData));
  });
};

/**
 * Capture and merge browser information into user profile data object.
 *
 * @function
 * @name captureBrowserInfo
 * @param {Object} userProfileData
 * @returns {void}
 */

const captureBrowserInfo = (userProfileData: ResponseBody): void => {
  const browserInfo = {
    userAgent: window.navigator.userAgent,
    browserType: detectBrowser(),
    language: document.documentElement.lang,
    refDomain: document.referrer,
    domain: getMainDomainFromCurrentUrl(),
  };
  Object.assign(userProfileData.browserInfo, browserInfo);
};

/**
 * Consolidating all functions that capture user profile data on page load into a single one.
 *
 * @function
 * @name captureDataElementsOnPageLoad
 * @param {Object} userProfileData
 * @returns {void}
 */
const captureDataElementsOnPageLoad = async (userProfileData: ResponseBody) => {
  captureBrowserInfo(userProfileData);
  captureCookies(userProfileData);
  captureConsentCookies(userProfileData);
  await captureAndSendGeoData(userProfileData);
};

/**
 * Handles visit session workflow - which expires every 4 hours.
 *
 * @function
 * @name checkUniversalIdVisitSession
 * @returns {void}
 */
const checkUniversalIdVisitSession = async () => {
  const visitSessionValue = localStorage.getItem('universalIdVisitSession');
  /*
   * Steps to handle the visitor session:
   * 1. Verify if a visit session exists in local storage.
   * 2. If the session exists:
   *    - Check if the current page is a part of a specific list of URLs (whitelisted).
   *    - Collect data such as browser details, cookies, and geolocation information.
   *    - Verify if a universal ID (uID) cookie is already set.
   *    - If no uID cookie is found, request and store a new one.
   * 3. Determine whether the session has expired based on the session's expiration time.
   * 4. Depending on the results of these checks, send and recieve data from Connector Grid.
   */
  if (visitSessionValue) {
    console.log("visit session exists!!!!!")
    const visitSessionValueJsonFormat = JSON.parse(visitSessionValue);
    const getCurrentTime = Date.now();
    const getExpirationTime = visitSessionValueJsonFormat.expirationTime;
    const uID = getCookieValue('uID');
    if (isWhitelisted(defaultUrlWhitelist)) {
      console.log("page is whitelisted!!!")
      await captureDataElementsOnPageLoad(userProfileData);
      !uID
        ? getUniversalIdCookie(userProfileData)
        : sendDataToConnectorGrid(userProfileData);
    } else if (!uID) {
      console.log("UID doesn't exist?")
      await captureDataElementsOnPageLoad(userProfileData);
      getUniversalIdCookie(userProfileData);
    } else if (getCurrentTime >= getExpirationTime) {
      await captureDataElementsOnPageLoad(userProfileData);
      localStorage.removeItem('universalIdVisitSession');
      setUniversalIdVisitSession();
      !uID
        ? getUniversalIdCookie(userProfileData)
        : sendDataToConnectorGrid(userProfileData);
    }
    /*
     * Handling the case where no visit session exists:
     * 1. Collect necessary data:
     *    - Browser-level information, cookies, and geolocation data.
     * 2. Check for the presence of a universal ID (uID) cookie:
     *    - If no uID cookie is found, request and store a new one.
     * 3. Based on the conditions, send and recieve the data from Connector Grid.
     */
  } else {
    setUniversalIdVisitSession();
    await captureDataElementsOnPageLoad(userProfileData);
    const uID = getCookieValue('uID');
    !uID
      ? getUniversalIdCookie(userProfileData)
      : sendDataToConnectorGrid(userProfileData);
  }
};



/* Checks if a keyboard event is either from the Enter key or Spacebar. 
   Used to filter out irrelevant keydown events for accessibility */ 
const isActivationKey = (e: KeyboardEvent) => e.key === 'Enter' || e.key === ' ';
const OT_ROOT = '#onetrust-consent-sdk';
/* Tracks the last consent string to avoid redundant api calls */ 
let lastConsent = '';


/* Tiny debounce utility for limiting the rate at which sendConsentSnapshot fires. 
   Prevents multiple rapid calls on repeated clicks from happening */
const microDebounce = <T extends (...args: any[]) => void>(fn: T): T => {
  let ready = true;
  return <T>((...args: Parameters<T>) => {
    if(!ready) return;
    ready = false;
    queueMicrotask(() => {
      ready = true;
      fn(...args)
    });
  });
};

/* 
  Send the consent snapshot if and only if consent value changed.
  - Captures consent cookies & user profile data
  - Only fires if OptanonConsent value changed
  - Sends a request to CG with updated consent preference
*/
const sendConsentSnapshot = microDebounce(async (userProfileData: ResponseBody) => {
  captureConsentCookies(userProfileData);
  const currentConsent = getCookieValue('OptanonConsent') ?? '';
  if(currentConsent === lastConsent) return;
  lastConsent = currentConsent;
  const uID = getCookieValue('uID');
  // const newUserProfileData = { ...userProfileData, universalIds: { ...userProfileData.universalIds, uID: uID } };
  await captureDataElementsOnPageLoad(userProfileData);
  !uID ? getUniversalIdCookie(userProfileData) : sendDataToConnectorGrid(userProfileData);
});

/* Ensures event listeners for OneTrust consent banner buttons are only attached once. 
   Tracks clicks/key-activation on Accept, Confirm, or Close */ 

let consentListenersAttached = false;
const attachConsentListenersOnce = (): void => {
  const OT_BTN_SELECTORS = [
    '#onetrust-accept-btn-handler',
    '.save-preference-btn-handler',
    '.onetrust-close-btn-handler'
  ].join(',');

  if(consentListenersAttached) return;

  // Listen for OneTrust consent changes, if available
  if(window.OneTrust?.OnConsentChanged) {
    window.OneTrust.OnConsentChanged(() => sendConsentSnapshot(userProfileData));
    consentListenersAttached = true;
    return;
  }

  // Fallback approach - event listeners
  const oneTrustConsentBanner = document.querySelector<HTMLElement>(OT_ROOT);
  if(!oneTrustConsentBanner) return;
  
  const handleConsentChange = (event: MouseEvent | KeyboardEvent) => {
    if(!(event.target instanceof HTMLElement)) return;
    const button = event.target;
    if(!button.matches(OT_BTN_SELECTORS)) return;
    if(event.type === 'keydown' && !isActivationKey(event as KeyboardEvent)) return;
    setTimeout(() => sendConsentSnapshot(userProfileData), 50);
  };

  oneTrustConsentBanner.addEventListener('click', handleConsentChange);
  oneTrustConsentBanner.addEventListener('keydown', handleConsentChange);
  consentListenersAttached = true;
};

/**
 * Waits for OptanonConsent cookie to appear in the browser (set by OneTrust). 
 * This function will check at small intervals to see if the OneTrust cookie exists in the browser.
 * Used to ensure that the user's choice has been recorded before firing API calls.
 *
 * @function
 * @name waitForConsentCookie
 * @param {number} maxMs - timeout max in ms (default 5000)
 * @param {number} step - poll interval in ms (default 100)
 * @returns {Promise<void>} - Resolves when cookie is present or timeout reached
 */
const waitForConsentCookie = (maxMs = 5000, step = 100): Promise<void> => {
  return new Promise((resolve) => {
    const start = performance.now();
    (function poll() {
      console.log('----------------document.cookie-----------', document.cookie);
      if (document.cookie.includes('OptanonConsent=') && document.cookie.includes('&groups=')) return resolve();
      if(performance.now() - start > maxMs) return resolve();
      setTimeout(poll, step);
    })();
    console.log('wait for consent cookie function fired, cookie contents: ' + getCookieValue('OptanonConsent'));
  });
};

/**
 * Main entrypoint: 
 * 1) Waits for OneTrust banner
 * 2) Attaches listeners 
 * 3) Waits for OneTrust consent cookie
 * 4) Then initiates the universal ID flow. 
 *
 * @function
 * @name initializeUniversalIdFlow
 * @returns {Promise<void>}
 */
async function initializeUniversalIdFlow(): Promise<void> {
  console.log("initialize universal id flow");
  try {
    await waitForOneTrustBanner(OT_ROOT, {timeoutMs: 5000});
    attachConsentListenersOnce();
    await waitForConsentCookie();
  } catch(error) {
    console.error("UniversalIdFlow pre-check crash: failed while waiting for Onetrust banner, or attaching consent.", error)
  }

  try {
    await checkUniversalIdVisitSession();
  } catch(error) {
    console.error("checkUniversalIdVisitSession() crashed: failed during visit/session check", error);
  }
}

if(document.readyState === 'complete') {
  void initializeUniversalIdFlow();
} else {
  window.addEventListener('load', () => void initializeUniversalIdFlow(), {
    once: true,
  });
}
